<template>
  <v-col cols="12">
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="4000"
      location="top"
      class="text-center"
    >
      {{ snackbar.text }}
    </v-snackbar>

    <v-card
      class="premium-features-card card-action"
      :class="{ 'premium-features-card--secondary': props.variant === 'secondary' }"
    >
      <v-card-title class="d-flex align-center text-subtitle-1 title-wrap">
        <v-icon icon="mdi-star" color="warning" class="mr-2"></v-icon>
        {{ authStore.credits > 0 ? $t('premium.title_with_credits') : $t('premium.title') }}
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="8">
            <div class="text-body-2 mb-4">
              {{ authStore.credits > 0 ? $t('premium.subtitle_with_credits', { credits: authStore.credits }) : $t('premium.subtitle') }}
            </div>
            <v-list>
              <v-list-item class="feature-item">
                <template v-slot:prepend>
                  <v-icon size="small" icon="mdi-check-circle" class="feature-icon"></v-icon>
                </template>
                <v-list-item-title>{{ $t('premium.features.analysis.title', { credits: pricingConfig?.credits }) }}</v-list-item-title>
                <v-list-item-subtitle>{{ $t('premium.features.analysis.description') }}</v-list-item-subtitle>
              </v-list-item>
              <v-list-item class="feature-item">
                <template v-slot:prepend>
                  <v-icon size="small" icon="mdi-brain" class="feature-icon"></v-icon>
                </template>
                <v-list-item-title>{{ $t('premium.features.ai.title') }}</v-list-item-title>
                <v-list-item-subtitle>{{ $t('premium.features.ai.description') }}</v-list-item-subtitle>
              </v-list-item>
              <v-list-item class="feature-item">
                <template v-slot:prepend>
                  <v-icon size="small" icon="mdi-clipboard-text-search" class="feature-icon"></v-icon>
                </template>
                <v-list-item-title>{{ $t('premium.features.recommendations.title') }}</v-list-item-title>
                <v-list-item-subtitle>{{ $t('premium.features.recommendations.description') }}</v-list-item-subtitle>
              </v-list-item>
              <v-list-item class="feature-item">
                <template v-slot:prepend>
                  <v-icon size="small" icon="mdi-cash-check" class="feature-icon"></v-icon>
                </template>
                <v-list-item-title>{{ $t('premium.features.cost.title') }}</v-list-item-title>
                <v-list-item-subtitle>{{ $t('premium.features.cost.description') }}</v-list-item-subtitle>
              </v-list-item>
              <v-list-item class="feature-item">
                <template v-slot:prepend>
                  <v-icon size="small" icon="mdi-floor-plan" class="feature-icon"></v-icon>
                </template>
                <v-list-item-title>{{ $t('premium.features.rooms.title') }}</v-list-item-title>
                <v-list-item-subtitle>{{ $t('premium.features.rooms.description') }}</v-list-item-subtitle>
              </v-list-item>
              <v-list-item class="feature-item">
                <template v-slot:prepend>
                  <v-icon size="small" icon="mdi-file-export" class="feature-icon"></v-icon>
                </template>
                <v-list-item-title>{{ $t('premium.features.export.title') }}</v-list-item-title>
                <v-list-item-subtitle>{{ $t('premium.features.export.description') }}</v-list-item-subtitle>
              </v-list-item>
              <v-list-item class="feature-item">
                <template v-slot:prepend>
                  <v-icon size="small" icon="mdi-headset" class="feature-icon"></v-icon>
                </template>
                <v-list-item-title>{{ $t('premium.features.support.title') }}</v-list-item-title>
                <v-list-item-subtitle>{{ $t('premium.features.support.description') }}</v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-col>
          <v-col cols="12" md="4" class="d-flex align-center justify-center">
            <v-btn
              color="primary"
              size="large"
              class="upgrade-btn text-body-2"
              @click="handleUpgradeClick"
            >
              <v-icon start icon="mdi-rocket-launch" class="mr-2"></v-icon>
              {{ $t('premium.upgradeButton') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <PayPalButton
      v-model="showPayPal"
      :source="props.trackingSource"
      @payment-success="handlePaymentSuccess"
      @payment-error="handlePaymentError"
    />
  </v-col>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import PayPalButton from '@/components/common/PayPalButton.vue'
import { useAuthStore } from '@/stores/auth'
import { useI18n } from 'vue-i18n'
import { getIdToken } from 'firebase/auth'
import { trackEvent } from '@/utils/analytics'

const props = defineProps({
  trackingSource: {
    type: String,
    default: 'premium_features',
  },
  variant: {
    type: String,
    default: 'default'
  },
})

const { t, locale } = useI18n()
const showPayPal = ref(false)
const authStore = useAuthStore()
const pricingConfig = ref(null)
const router = useRouter()

const fetchPricingConfig = async () => {
  try {
    let headers = {};
    if (authStore.isAuthenticated) {
      const token = await getIdToken(authStore.user);
      headers.Authorization = `Bearer ${token}`;
    }
    const response = await fetch(`/api/pricing?locale=${locale.value}`, { headers });
    pricingConfig.value = await response.json();
  } catch (error) {
    console.error('Failed to fetch pricing:', error);
    showNotification(t('pricing.payment.error'), 'error');
  }
}

onMounted(() => {
  fetchPricingConfig();
});

const snackbar = ref({
  show: false,
  text: '',
  color: 'success'
})

const handleUpgradeClick = async () => {
  if (!authStore.isAuthenticated) {
    await router.push('/');
    setTimeout(() => {
      const pricingElement = document.getElementById('pricing');
      if (pricingElement) {
        pricingElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
    return;
  }
  // Track premium upgrade button click
  trackEvent('premium_upgrade_click', {
    location: props.trackingSource,
    user_credits: authStore.credits
  });
  showPayPal.value = true;
}

const handlePaymentSuccess = () => {
  showNotification(t('pricing.payment.success'), 'success');
  authStore.fetchCredits();
}

const handlePaymentError = (error) => {
  // Track payment error from premium features
  trackEvent('payment_error', {
    location: props.trackingSource,
    error_message: error?.message || 'Unknown error'
  });
  console.error('Payment failed:', error);
  showNotification(t('pricing.payment.error'), 'error');
}

const showNotification = (text, color = 'success') => {
  snackbar.value = {
    show: true,
    text,
    color
  }
}
</script>

<style scoped>
.premium-features-card {
}

.premium-features-card--secondary {
  border: 1px solid rgba(41, 70, 86, 0.12);
  background: rgba(255, 255, 255, 0.62);
  box-shadow: none !important;
}

.premium-features-card--secondary :deep(.v-card-title) {
  font-size: var(--font-size-body-1);
}

.premium-features-card--secondary :deep(.v-list) {
  background: transparent;
}

.upgrade-btn {
  min-width: 200px;
  background-color: var(--primary) !important;
  color: white !important;
}

.upgrade-btn:hover {
  background-color: var(--primary-dark) !important;
}

/* Add title wrapping */
:deep(.title-wrap) {
  white-space: normal !important;
  overflow-wrap: break-word !important;
  word-wrap: break-word !important;
  line-height: 1.4;
  font-weight: var(--font-weight-semibold) !important;
}

/* Improve text wrapping for list items */
:deep(.v-list-item) {
  white-space: normal;
  height: auto !important;
  min-height: 48px;
  padding: 12px 0;
}

/* Remove font-specific styles and use Vuetify's typography classes */
:deep(.v-list-item-title) {
  white-space: normal !important;
  overflow-wrap: break-word !important;
  word-wrap: break-word !important;
  hyphens: auto;
  color: var(--text-primary);
  font-weight: var(--font-weight-medium);
  line-height: 1.4;
  margin-bottom: 4px;
}

:deep(.v-list-item-subtitle) {
  white-space: normal !important;
  overflow-wrap: break-word !important;
  word-wrap: break-word !important;
  -webkit-line-clamp: unset !important;
  -webkit-box-orient: unset !important;
  color: var(--text-secondary) !important;
  line-height: 1.5;
}

/* Add responsive padding */
@media (max-width: 600px) {
  :deep(.v-card-text) {
    padding: 16px;
  }
  
  :deep(.v-list-item) {
    padding: 8px 0;
  }
  
  .upgrade-btn {
    width: 100%;
    margin-top: 16px;
  }
}

/* Add PayPal modal styles */
.paypal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.paypal-modal {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  max-width: 500px;
  width: 90%;
  position: relative;
}

.close-button {
  position: absolute;
  top: 8px;
  right: 8px;
}

/* Improve list item layout */
:deep(.feature-item) {
  padding-left: 0;
  column-gap: 8px;
}

:deep(.feature-icon) {
  margin-right: 4px;
  color: var(--primary);
}

/* Update responsive styles */
@media (max-width: 600px) {
  :deep(.v-card-text) {
    padding-left: 16px;
  }
  
  :deep(.v-list-item) {
    padding: 4px 0 0;
  }
  
  :deep(.feature-item) {
    padding-left: 0;
    column-gap: 4px;
  }
  
  :deep(.feature-icon) {
    margin-right: 2px;
  }

  :deep(.v-list-item__spacer) {
    width: 6px !important;
  }
  
  :deep(.v-list-item__prepend) {
    padding-inline-end: 0px;
  }

  .upgrade-btn {
    width: 100%;
    margin-top: 16px;
  }
}
</style>
